"use client";

import { useCallback, useState } from "react";
import { AuthField } from "~/components/auth/AuthField";
import { AuthPanel } from "~/components/auth/AuthPanel";
import { OtpVerification } from "~/components/auth/OtpVerification";
import { PhoneField } from "~/components/auth/PhoneField";
import { Button } from "~/components/ui/button";
import { Paragraph } from "~/components/ui/Typography";
import { authClient } from "~/lib/auth/client";
import { isValidE164Phone, isValidEmail } from "~/lib/auth/validation";
import { api } from "~/trpc/react";
type SecondaryContactStepProps = {
  /** Which contact to collect */
  collect: "email" | "phone";
  /** When true, the contact is mandatory — hides the skip button */
  required?: boolean;
  /** Pre-fill the field — e.g. an existing email that still needs verifying */
  initialValue?: string;
  onComplete: () => void | Promise<void>;
  onSkip: () => void;
};
type Phase = "collect" | "verify-phone" | "verify-email";
function parseMutationError(err: unknown, field: "email" | "phone"): string {
  const raw = err instanceof Error ? err.message : "";
  let inner = raw;
  try {
    const body = JSON.parse(raw) as {
      message?: string;
    };
    inner = body.message ?? raw;
  } catch {
    /* raw was not JSON */
  }
  if (/email_exists|phone_exists/i.test(inner) || /already (been registered|in use|exists|taken)/i.test(inner)) {
    return `This ${field === "email" ? "email address" : "phone number"} is already linked to another account. Please use a different one.`;
  }
  return inner || "Something went wrong. Please try again.";
}
export function SecondaryContactStep({
  collect,
  required = false,
  initialValue = "",
  onComplete,
  onSkip
}: SecondaryContactStepProps) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phase, setPhase] = useState<Phase>("collect");
  const utils = api.useUtils();
  const changeEmail = api.user.changeEmail.useMutation();
  const changePhone = api.user.changePhone.useMutation();
  const isEmail = collect === "email";
  const validate = (): string | undefined => {
    if (!value.trim()) {
      return isEmail ? "Email is required." : "Phone number is required.";
    }
    if (isEmail && !isValidEmail(value)) {
      return "Please enter a valid email address.";
    }
    if (!isEmail && !isValidE164Phone(value)) {
      return "Please enter a valid 10-digit phone number.";
    }
    return undefined;
  };
  const handleContinue = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setIsSubmitting(true);
    try {
      if (isEmail) {
        await changeEmail.mutateAsync({
          email: value.trim()
        });
        setPhase("verify-email");
      } else {
        await changePhone.mutateAsync({
          phone: value
        });
        setPhase("verify-phone");
      }
    } catch (err) {
      setError(parseMutationError(err, isEmail ? "email" : "phone"));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleVerifyPhone = useCallback(async (code: string) => {
    await authClient.verifyContactChange("phone", value, code);
    await authClient.refresh();
    // Await so the OTP spinner stays until promote + navigation begin.
    await onComplete();
  }, [value, onComplete]);
  const handleResendPhone = useCallback(async () => {
    await changePhone.mutateAsync({
      phone: value
    });
  }, [value, changePhone]);
  const handleVerifyEmail = useCallback(async (code_0: string) => {
    await authClient.verifyContactChange("email", value, code_0);
    await authClient.refresh();
    await utils.user.getUser.invalidate();
    // Await so the OTP spinner stays until promote + navigation begin.
    await onComplete();
  }, [value, onComplete, utils]);
  const handleResendEmail = useCallback(async () => {
    await changeEmail.mutateAsync({
      email: value.trim()
    });
  }, [value, changeEmail]);
  const handleValueChange = (v: string) => {
    setValue(v);
    if (error) setError("");
  };
  const handleBackToCollect = () => setPhase("collect");
  if (phase === "verify-phone") {
    return <OtpVerification identifier={value} channel="phone" onVerify={handleVerifyPhone} onResend={handleResendPhone} onBack={handleBackToCollect} />;
  }
  if (phase === "verify-email") {
    return <OtpVerification identifier={value} channel="email" onVerify={handleVerifyEmail} onResend={handleResendEmail} onBack={handleBackToCollect} />;
  }
  return <AuthPanel title={isEmail ? "Add your email" : "Add a recovery phone"} subtitle={isEmail ? "An email is required to secure your account and receive booking confirmations." : "Secure your account by adding a backup phone number for recovery."}>
      <div className="mb-3 sm:mb-4">
        {isEmail ? <AuthField id="secondary-email" label="Email address" type="email" placeholder="joelovesgoodparking@example.com" autoComplete="email" value={value} error={error} onChange={handleValueChange} /> : <PhoneField id="secondary-phone" label="Phone number" value={value} error={error} onChange={handleValueChange} />}
      </div>

      {isEmail ? null : <Paragraph size="sm" className="mb-3 text-center text-muted">
          By continuing, you agree to receive calls or texts from EZSpot.
          Message and data rates may apply.
        </Paragraph>}

      <Button className="mb-3 w-full" disabled={isSubmitting} onClick={handleContinue} type="button" variant="primary" size="lg">
        {isSubmitting ? "Sending..." : "Send verification code"}
      </Button>

      {required ? null : <Button className="w-full" variant="ghost" size="sm" onClick={onSkip} type="button">
          I'll do this later
        </Button>}
    </AuthPanel>;
}